import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../config';

// Collection reference
const blogCollectionRef = collection(db, 'blogPosts');
const categoriesCollectionRef = collection(db, 'blogCategories');

// Create a new blog post
export const createBlogPost = async (postData, featuredImage = null) => {
  try {
    // If there's a featured image, upload it first
    let imageUrl = '';
    if (featuredImage) {
      const imageRef = ref(storage, `blog/${Date.now()}_${featuredImage.name}`);
      await uploadBytes(imageRef, featuredImage);
      imageUrl = await getDownloadURL(imageRef);
    }

    // Add the blog post to Firestore
    const docRef = await addDoc(blogCollectionRef, {
      ...postData,
      featuredImage: imageUrl,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return { id: docRef.id };
  } catch (error) {
    console.error('Error creating blog post:', error);
    throw error;
  }
};

// Update a blog post
export const updateBlogPost = async (postId, postData, featuredImage = null) => {
  try {
    const postRef = doc(db, 'blogPosts', postId);
    const postSnap = await getDoc(postRef);
    
    if (!postSnap.exists()) {
      throw new Error('Blog post not found');
    }
    
    let imageUrl = postSnap.data().featuredImage || '';
    
    // If a new image is provided, upload it and update the URL
    if (featuredImage) {
      // Delete old image if it exists
      if (imageUrl) {
        try {
          const oldImageRef = ref(storage, imageUrl);
          await deleteObject(oldImageRef);
        } catch (error) {
          console.warn('Error deleting old image:', error);
        }
      }
      
      // Upload new image
      const imageRef = ref(storage, `blog/${Date.now()}_${featuredImage.name}`);
      await uploadBytes(imageRef, featuredImage);
      imageUrl = await getDownloadURL(imageRef);
    }
    
    // Update the blog post
    await updateDoc(postRef, {
      ...postData,
      featuredImage: imageUrl,
      updatedAt: serverTimestamp()
    });
    
    return { id: postId };
  } catch (error) {
    console.error('Error updating blog post:', error);
    throw error;
  }
};

// Delete a blog post
export const deleteBlogPost = async (postId) => {
  try {
    const postRef = doc(db, 'blogPosts', postId);
    const postSnap = await getDoc(postRef);
    
    if (!postSnap.exists()) {
      throw new Error('Blog post not found');
    }
    
    // Delete featured image if it exists
    const imageUrl = postSnap.data().featuredImage;
    if (imageUrl) {
      try {
        const imageRef = ref(storage, imageUrl);
        await deleteObject(imageRef);
      } catch (error) {
        console.warn('Error deleting image:', error);
      }
    }
    
    // Delete the blog post
    await deleteDoc(postRef);
    
    return { success: true };
  } catch (error) {
    console.error('Error deleting blog post:', error);
    throw error;
  }
};

// Get a single blog post by ID
export const getBlogPost = async (postId) => {
  try {
    console.log(`Getting blog post with ID: ${postId}`);
    const postRef = doc(db, 'blogPosts', postId);
    const postSnap = await getDoc(postRef);
    
    if (!postSnap.exists()) {
      console.error(`Blog post with ID ${postId} not found`);
      throw new Error('Blog post not found');
    }
    
    const data = postSnap.data();
    console.log('Raw post data from Firestore:', data);
    
    // Convert Firestore Timestamp objects to dates for proper serialization
    const processedData = {
      id: postSnap.id,
      ...data,
      createdAt: data.createdAt || null,
      updatedAt: data.updatedAt || null
    };
    
    console.log('Processed post data:', processedData);
    return processedData;
  } catch (error) {
    console.error('Error getting blog post:', error);
    throw error;
  }
};

// Get all blog posts
export const getAllBlogPosts = async () => {
  try {
    const q = query(blogCollectionRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const posts = [];
    querySnapshot.forEach((doc) => {
      posts.push({ id: doc.id, ...doc.data() });
    });
    
    return posts;
  } catch (error) {
    console.error('Error getting blog posts:', error);
    throw error;
  }
};

// Get blog posts by category
export const getBlogPostsByCategory = async (categoryId) => {
  try {
    const q = query(
      blogCollectionRef, 
      where('categories', 'array-contains', categoryId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    const posts = [];
    querySnapshot.forEach((doc) => {
      posts.push({ id: doc.id, ...doc.data() });
    });
    
    return posts;
  } catch (error) {
    console.error('Error getting posts by category:', error);
    throw error;
  }
};

// Get recent posts
export const getRecentPosts = async (count = 5) => {
  try {
    const q = query(
      blogCollectionRef,
      orderBy('createdAt', 'desc'),
      limit(count)
    );
    const querySnapshot = await getDocs(q);
    
    const posts = [];
    querySnapshot.forEach((doc) => {
      posts.push({ id: doc.id, ...doc.data() });
    });
    
    return posts;
  } catch (error) {
    console.error('Error getting recent posts:', error);
    throw error;
  }
};

// Get related posts (posts with same category)
export const getRelatedPosts = async (postId, categoryIds, count = 3) => {
  try {
    // Get the current post to exclude it
    const currentPostRef = doc(db, 'blogPosts', postId);
    
    // Query posts with matching categories
    const q = query(
      blogCollectionRef,
      where('categories', 'array-contains-any', categoryIds),
      orderBy('createdAt', 'desc'),
      limit(count + 1) // Fetch one extra to account for current post
    );
    
    const querySnapshot = await getDocs(q);
    
    const posts = [];
    querySnapshot.forEach((doc) => {
      // Exclude the current post
      if (doc.id !== postId) {
        posts.push({ id: doc.id, ...doc.data() });
      }
    });
    
    // Limit to requested count
    return posts.slice(0, count);
  } catch (error) {
    console.error('Error getting related posts:', error);
    throw error;
  }
};

// Category management
export const createCategory = async (categoryData) => {
  try {
    const docRef = await addDoc(categoriesCollectionRef, {
      ...categoryData,
      createdAt: serverTimestamp()
    });
    
    return { id: docRef.id };
  } catch (error) {
    console.error('Error creating category:', error);
    throw error;
  }
};

export const updateCategory = async (categoryId, categoryData) => {
  try {
    const categoryRef = doc(db, 'blogCategories', categoryId);
    await updateDoc(categoryRef, categoryData);
    
    return { id: categoryId };
  } catch (error) {
    console.error('Error updating category:', error);
    throw error;
  }
};

export const deleteCategory = async (categoryId) => {
  try {
    await deleteDoc(doc(db, 'blogCategories', categoryId));
    return { success: true };
  } catch (error) {
    console.error('Error deleting category:', error);
    throw error;
  }
};

export const getAllCategories = async () => {
  try {
    const q = query(categoriesCollectionRef, orderBy('name', 'asc'));
    const querySnapshot = await getDocs(q);
    
    const categories = [];
    querySnapshot.forEach((doc) => {
      categories.push({ id: doc.id, ...doc.data() });
    });
    
    return categories;
  } catch (error) {
    console.error('Error getting categories:', error);
    throw error;
  }
}; 